/**
 * REGÍMENES ESPECIALES - Docentes, Insalubres y otros
 * 
 * Características:
 * - Reducción de edad y/o aportes
 * - Cálculos específicos por régimen
 * - Aplicable tanto en ANSES como en IPS-BA
 */

/**
 * REGIMENES config table — single source of truth for all regimes
 *
 * Keys are machine-readable identifiers, values contain:
 * - id: unique identifier
 * - label/labelEs: display names
 * - edadMinima: { M: age, F: age }
 * - aniosAportes: required years of contributions
 */

export const REGIMENES = {
  COMUN: { id: 'comun', label: 'Común', labelEs: 'Común', edadMinima: { M: 65, F: 60 }, aniosAportes: 30 },
  DOCENTE: { id: 'docente', label: 'Docente', labelEs: 'Docente', edadMinima: { M: 57, F: 55 }, aniosAportes: 25, movilidad: 'RIPDOC' },
  INSALUBRE: { id: 'insalubre', label: 'Insalubre', labelEs: 'Insalubre', edadMinima: { M: 52, F: 50 }, aniosAportes: 25 },
  CONSTRUCCION: { id: 'construccion', label: 'Construcción', labelEs: 'Construcción', edadMinima: { M: 65, F: 60 }, aniosAportes: 30 },
  AGRARIO: { id: 'agrario', label: 'Agrario', labelEs: 'Agrario', edadMinima: { M: 65, F: 60 }, aniosAportes: 30 },
  SERVICIO_DOMESTICO: { id: 'servicio_domestico', label: 'Servicio Doméstico', labelEs: 'Servicio Doméstico', edadMinima: { M: 65, F: 60 }, aniosAportes: 30 },
}

export const REGIMENES_LIST = Object.values(REGIMENES)

export function getRegimenById(id) {
  return REGIMENES_LIST.find(r => r.id === id) || REGIMENES.COMUN
}

/**
 * RÉGIMEN DOCENTE
 * 
 * Ley 14.473 (Nacional) + equivalentes provinciales
 * 
 * Características:
 * - Edad reducida: 55-57 años (mujeres-hombres)
 * - Aportes: 30 años (nacional) o 25-27 años (provincial)
 * - Se jubilan más rápido que público general
 * 
 * Cálculo:
 * - ANSES: Mismo método (PBU+PC+PAP) pero con base de 100 últimas remuneraciones
 * - IPS: 70-82% mejor cargo similar pero con base de últimos 36/60 meses
 */

export const REGIMEN_DOCENTE = {
  nombre: 'DOCENTE',
  edadMinima: { F: 55, M: 57 },
  aportesMinimos: { NACIONAL: 30, PROVINCIAL: 25 },
  base: 100, // Remuneraciones base para promedio (vs 120 en público)
  descripcion: 'Régimen Docente - Jubilación anticipada por profesión'
}

/**
 * RÉGIMEN INSALUBRE
 * 
 * Ley 18.038 + cajas provinciales
 * 
 * Características:
 * - Edad muy reducida: 50-52 años (mujeres-hombres)
 * - Aportes: 25-27 años
 * - Para trabajos peligrosos o nocivos
 * 
 * Profesiones típicas:
 * - Minería
 * - Trabajos químicos/tóxicos
 * - Trabajos con radiación
 * - Policía/Seguridad
 */

export const REGIMEN_INSALUBRE = {
  nombre: 'INSALUBRE',
  edadMinima: { F: 50, M: 52 },
  aportesMinimos: 25,
  base: 100,
  descripcion: 'Régimen Insalubre - Jubilación anticipada por tareas peligrosas'
}

/**
 * Valida si aplica Régimen Docente
 * @param {number} edadActual
 * @param {number} anosAporte
 * @param {string} sexo
 * @returns {Object} {aplica, razon}
 */
export function validarRegimenDocente(edadActual, anosAporte, sexo) {
  const edadReq = REGIMEN_DOCENTE.edadMinima[sexo] || REGIMEN_DOCENTE.edadMinima.M
  const aportesReq = REGIMEN_DOCENTE.aportesMinimos.PROVINCIAL

  const cumpleEdad = edadActual >= edadReq
  const cumpleAportes = anosAporte >= aportesReq

  if (cumpleEdad && cumpleAportes) {
    return {
      aplica: true,
      razon: `Cumple Régimen Docente: ${edadActual.toFixed(1)} años (≥${edadReq}) + ${anosAporte.toFixed(1)} años (≥${aportesReq})`
    }
  }

  return {
    aplica: false,
    razon: `No cumple Régimen Docente. Edad: ${edadActual.toFixed(1)}/${edadReq}. Aportes: ${anosAporte.toFixed(1)}/${aportesReq}.`
  }
}

/**
 * Valida si aplica Régimen Insalubre
 * @param {number} edadActual
 * @param {number} anosAporte
 * @param {string} sexo
 * @returns {Object} {aplica, razon}
 */
export function validarRegimenInsalubre(edadActual, anosAporte, sexo) {
  const edadReq = REGIMEN_INSALUBRE.edadMinima[sexo] || REGIMEN_INSALUBRE.edadMinima.M
  const aportesReq = REGIMEN_INSALUBRE.aportesMinimos

  const cumpleEdad = edadActual >= edadReq
  const cumpleAportes = anosAporte >= aportesReq

  if (cumpleEdad && cumpleAportes) {
    return {
      aplica: true,
      razon: `Cumple Régimen Insalubre: ${edadActual.toFixed(1)} años (≥${edadReq}) + ${anosAporte.toFixed(1)} años (≥${aportesReq})`
    }
  }

  return {
    aplica: false,
    razon: `No cumple Régimen Insalubre. Edad: ${edadActual.toFixed(1)}/${edadReq}. Aportes: ${anosAporte.toFixed(1)}/${aportesReq}.`
  }
}

/**
 * Identifica PROFESIONES INSALUBRES
 * 
 * Base de datos de trabajos que califican como insalubres
 * @param {string} profesion - "POLICIA", "MINERO", "QUIMICO", etc
 * @returns {Object} {esInsalubre, categoria, descripcion}
 */
export function identificarProfesionInsalubre(profesion) {
  const profisionesInsalubres = {
    POLICIA: {
      esInsalubre: true,
      categoria: 'SEGURIDAD',
      descripcion: 'Fuerzas de seguridad - tareas de riesgo permanente'
    },
    MINERO: {
      esInsalubre: true,
      categoria: 'EXTRACTIVAS',
      descripcion: 'Minería - exposición a polvo, gases, radiación'
    },
    QUIMICO: {
      esInsalubre: true,
      categoria: 'QUIMICA',
      descripcion: 'Industria química - exposición a sustancias tóxicas'
    },
    BOMBERO: {
      esInsalubre: true,
      categoria: 'SEGURIDAD',
      descripcion: 'Bomberos - riesgo de vida permanente'
    },
    SANITARIO: {
      esInsalubre: true,
      categoria: 'SALUD',
      descripcion: 'Personal de salud - exposición biológica'
    },
    PENITENCIARIO: {
      esInsalubre: true,
      categoria: 'SEGURIDAD',
      descripcion: 'Personal penitenciario - violencia, estrés extremo'
    },
    AGRICULTOR: {
      esInsalubre: true,
      categoria: 'AGRICOLA',
      descripcion: 'Agricultura - exposición a pesticidas/químicos'
    },
    CONSTRUCTOR: {
      esInsalubre: false,
      categoria: 'CONSTRUCCION',
      descripcion: 'Construcción - casos específicos según tareas'
    },
    ADMINISTRATIVO: {
      esInsalubre: false,
      categoria: 'ADMINISTRATIVO',
      descripcion: 'Administrativo - no califica como insalubre'
    }
  }

  const prof = profisionesInsalubres[profesion.toUpperCase()] || {
    esInsalubre: false,
    categoria: 'DESCONOCIDA',
    descripcion: 'Profesión no identificada'
  }

  return {
    profesion,
    ...prof
  }
}

/**
 * RÉGIMEN DE EDAD AVANZADA
 * 
 * Ley especial: Jubilación sin requisito de aportes si tienes edad muy avanzada
 * - Hombres: 70+ años
 * - Mujeres: 65+ años
 */

export const REGIMEN_EDAD_AVANZADA = {
  nombre: 'EDAD_AVANZADA',
  edadMinima: { F: 65, M: 70 },
  aportesMinimos: 0, // Sin requisito
  descripcion: 'Régimen de Edad Avanzada - Sin requisito de aportes'
}

export function validarRegimenEdadAvanzada(edadActual, sexo) {
  const edadReq = REGIMEN_EDAD_AVANZADA.edadMinima[sexo] || REGIMEN_EDAD_AVANZADA.edadMinima.M

  const cumple = edadActual >= edadReq

  return {
    aplica: cumple,
    razon: cumple
      ? `Cumple Edad Avanzada: ${edadActual.toFixed(1)} años (≥${edadReq}). Sin requisito de aportes.`
      : `No cumple Edad Avanzada. Faltan ${(edadReq - edadActual).toFixed(1)} años.`
  }
}

/**
 * MATRIZ DE DETECCIÓN DE RÉGIMEN ESPECIAL
 * 
 * Determina automáticamente qué régimen especial aplica (si alguno)
 * 
 * @param {Object} parametros
 * @property {number} edadActual
 * @property {number} anosAporte
 * @property {string} sexo
 * @property {string} profesion - "DOCENTE", "POLICIA", etc
 * 
 * @returns {Object} {regimenAplicable, opciones, recomendacion}
 */
export function detectarRegimenEspecial(parametros) {
  const { edadActual, anosAporte, sexo, profesion } = parametros

  const opciones = []

  // Régimen Edad Avanzada (aplicable a todos)
  const edadAvanzada = validarRegimenEdadAvanzada(edadActual, sexo)
  if (edadAvanzada.aplica) {
    opciones.push({
      regimen: 'EDAD_AVANZADA',
      aplica: true,
      razon: edadAvanzada.razon,
      prioridad: 1
    })
  }

  // Régimen Insalubre (si profesión lo indica)
  if (profesion) {
    const profInsalubre = identificarProfesionInsalubre(profesion)
    if (profInsalubre.esInsalubre) {
      const insalubre = validarRegimenInsalubre(edadActual, anosAporte, sexo)
      if (insalubre.aplica) {
        opciones.push({
          regimen: 'INSALUBRE',
          aplica: true,
          razon: insalubre.razon,
          profesion: profInsalubre.descripcion,
          prioridad: 2
        })
      }
    }
  }

  // Régimen Docente
  const docente = validarRegimenDocente(edadActual, anosAporte, sexo)
  if (docente.aplica) {
    opciones.push({
      regimen: 'DOCENTE',
      aplica: true,
      razon: docente.razon,
      prioridad: 3
    })
  }

  // Determinar recomendación (mejor opción)
  let recomendacion = null
  if (opciones.length > 0) {
    opciones.sort((a, b) => a.prioridad - b.prioridad)
    recomendacion = opciones[0]
  }

  return {
    tieneRegimentEspecial: opciones.length > 0,
    opcionesDisponibles: opciones,
    recomendacion,
    mensaje: opciones.length > 0
      ? `Calificas para régimen especial: ${recomendacion.regimen}`
      : 'No calificas para regímenes especiales. Aplican requisitos generales.'
  }
}

/**
 * PROYECCIÓN DE MEJORA POR RÉGIMEN ESPECIAL
 * 
 * Si el usuario NO cumple ordinariamente, pero SÍ cumple con especial:
 * ¿Cuánto falta para jubilarse?
 * 
 * @param {Object} evaluacion
 * @property {number} edadActual
 * @property {number} anosAporte
 * @property {string} sexo
 * 
 * @returns {Object} {diasFaltaOdinario, diasFaltaEspecial, recomendacion}
 */
export function proyectarMejoraPorEspecial(evaluacion) {
  const { edadActual, anosAporte, sexo } = evaluacion

  // Ordinario
  const edadOrd = sexo === 'F' ? 60 : 65
  const diasFaltaEdadOrd = Math.max(0, (edadOrd - edadActual) * 365)
  const diasFaltaAportesOrd = Math.max(0, (30 - anosAporte) * 365)
  const diasFaltaOdinario = Math.max(diasFaltaEdadOrd, diasFaltaAportesOrd)

  // Edad Avanzada
  const edadAvz = sexo === 'F' ? 65 : 70
  const diasFaltaAvz = Math.max(0, (edadAvz - edadActual) * 365)

  // Insalubre
  const edadIns = sexo === 'F' ? 50 : 52
  const diasFaltaIns = Math.max(0, (edadIns - edadActual) * 365)
  const diasFaltaAportesIns = Math.max(0, (25 - anosAporte) * 365)
  const diasFaltaEspecial = Math.max(diasFaltaIns, diasFaltaAportesIns)

  const ahorro = diasFaltaOdinario - diasFaltaEspecial
  const ahorroAnos = Math.round((ahorro / 365) * 100) / 100

  return {
    diasFaltaOdinario,
    diasFaltaEspecial,
    ahorro,
    ahorroEnAnos: ahorroAnos,
    recomendacion: `Si calificas para régimen especial, podrías jubilarte ${ahorroAnos.toFixed(2)} años antes.`
  }
}

/**
 * RÉGIMEN CONSTRUCCIÓN
 *
 * Ley 26.844 (trabajadores de la construcción)
 *
 * Características:
 * - Edad: 60-65 años (mujeres-hombres)
 * - Aportes: 30 años
 * - Para trabajadores registrados de la construcción
 */
export const REGIMEN_CONSTRUCCION = {
  nombre: 'CONSTRUCCION',
  edadMinima: { F: 60, M: 65 },
  aportesMinimos: 30,
  base: 100,
  descripcion: 'Régimen Construcción - Ley 26.844'
}

/**
 * RÉGIMEN AGRARIO
 *
 * Ley 26.727 (trabajadores agrarios)
 *
 * Características:
 * - Edad: 60-65 años (mujeres-hombres)
 * - Aportes: 30 años
 * - Para trabajadores rurales registrados
 */
export const REGIMEN_AGRARIO = {
  nombre: 'AGRARIO',
  edadMinima: { F: 60, M: 65 },
  aportesMinimos: 30,
  base: 100,
  descripcion: 'Régimen Agrario - Ley 26.727'
}

/**
 * RÉGIMEN SERVICIO DOMÉSTICO
 *
 * Ley 26.844 (personal de casas particulares)
 *
 * Características:
 * - Edad: 60-65 años (mujeres-hombres)
 * - Aportes: 30 años
 * - Para trabajadoras/es de casas particulares
 */
export const REGIMEN_SERVICIO_DOMESTICO = {
  nombre: 'SERVICIO_DOMESTICO',
  edadMinima: { F: 60, M: 65 },
  aportesMinimos: 30,
  base: 100,
  descripcion: 'Régimen Servicio Doméstico - Ley 26.844'
}

// Backward-compatible string constant exports
export const COMUN = 'comun'
export const DOCENTE = 'docente'
export const INSALUBRE = 'insalubre'
export const CONSTRUCCION = 'construccion'
export const AGRARIO = 'agrario'
export const SERVICIO_DOMESTICO = 'servicio_domestico'
